import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import { editCarModel, getCarModelsList, updateCarModelFilters } from '../../actions/carmodel.actions';
import {
  carModelEditRoles,
  CarModelListFilters,
  CarModelModel,
  CarModelRankItem,
  CarModelRequest
} from '../../../../api/core/core.types';
import Select from '../../../../components/forms/Select';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { CarModelActionTypes } from '../../types/carmodel.types';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { withTranslation, WithTranslation } from 'react-i18next';
import ListViewTable from '../../../../components/tables/ListViewTable';

interface CarModelProps extends RouteComponentProps, WithTranslation {
  getCarModelsList: (countRatePlans?: boolean, filters?: CarModelListFilters, page?: PageRequest) => Promise<void>;
  updateCarModelFilters: (filters: CarModelListFilters) => void;
  editCarModel: (id: number, request: CarModelRequest) => void;
  carModels: CarModelModel[] | null;
  user: UserModel | null;
  totalPages: number;
  totalElements: number;
  filters: CarModelListFilters | null;
}

interface CarModelState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filtersUpdated: boolean;
  filter: {
    combinedFieldFilter: {
      value: string;
    };
    hasPriceFilter: {
      value: { label: string; value: string | undefined }[];
    };
    tag: {
      value: string
    }
  };
  changedRankValue?: CarModelRankItem;
}

const UPDATE_RANK_VALUE_TIME_DELAY = 400;

const CarModelSortFields = [
  { value: 'name', label: 'NAME' },
  { value: 'rank', label: 'RANK' },
  { value: 'acrissCode', label: 'ACRISS_CODE' }
];

class CarModels extends React.Component<CarModelProps, CarModelState> {
  constructor(props: CarModelProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'rank',
      filtersUpdated: false,
      filter: {
        combinedFieldFilter: {
          value: ''
        },
        hasPriceFilter: {
          value: []
        },
        tag: {
          value: ''
        }
      },
      changedRankValue: undefined
    };
    this.rankInputRef = React.createRef();
  }

  rankInputRef?: any;

  async componentDidMount() {
    this.updateFilterValues();
    await this.props.getCarModelsList(true, this.props.filters ? this.props.filters : undefined);
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) this.updateFilterValues();
  }

  updateFilterValues = () => {
    this.setState({ filtersUpdated: true });
    const filters = this.props.filters;
    if (filters) {
      const selectedHasPriceOptions: { value: string | undefined; label: string }[] = [];
      if (filters.hasPrice === undefined) selectedHasPriceOptions.push({ value: undefined, label: 'HAS PRICE' });
      else if (filters.hasPrice === 'false') selectedHasPriceOptions.push({ value: 'false', label: 'ALTERNATIVE' });

      this.setState({
        selectedPageNumber: filters.pageNumber ? filters.pageNumber : 1,
        filtersUpdated: true,
        filter: {
          combinedFieldFilter: {
            value: filters.combinedFieldFilter ? filters.combinedFieldFilter : ''
          },
          hasPriceFilter: {
            value: selectedHasPriceOptions
          },
          tag: {
            value: filters?.tag ?? ''
          }
        }
      });
    }
  };

  clearFilters = async () => {
    await this.setState({
      selectedPageNumber: 1,
      filtersUpdated: true,
      filter: {
        combinedFieldFilter: {
          value: ''
        },
        hasPriceFilter: {
          value: [ { value: undefined, label: 'HAS PRICE' } ]
        },
        tag: {
          value: ''
        }
      }
    });

    const filters = this.composeCarModelFilters();
    this.props.updateCarModelFilters(filters);
    this.props.getCarModelsList(true);
  };

  onFilterClick = async (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeCarModelFilters();
    await this.props.getCarModelsList(true, filters);
    this.props.updateCarModelFilters(filters);
  };

  composeCarModelFilters = () => {
    const hasPrice: any = this.state.filter.hasPriceFilter.value;

    const filters: CarModelListFilters = {
      combinedFieldFilter: this.state.filter.combinedFieldFilter.value,
      tag: this.state.filter.tag.value,
      hasPrice: hasPrice.value === undefined ? undefined : 'false'
    };

    return filters;
  };

  onRowClick = (row: any) => (
    this.props.history.push(`/setup/carmodels/${row.original.id}/view`));


  statusCell = (props: any) => (<StatusLabel status={props}/>);

  rankCell = ({ cell }: { cell: any }) => {
    return <div className="w-100 h-100" onClick={(e) => e.stopPropagation()}>
      {(!this.state.changedRankValue || this.state.changedRankValue.id !== cell.row.original.id) ?
        <div className="editable-cell"
             onClick={(e) => {
               e.stopPropagation();
               this.addRankItemWithIdAndValue(cell.row.original.id, cell.row.original, cell.row.original.rank);
             }}>
          {(cell.row.original.rank || cell.row.original.rank === 0) ? cell.row.original.rank : ' '}
        </div>
        :
        <div className={'editable-cell-change-input d-flex w-100 editable-cell'}>
          <input value={this.state.changedRankValue.value} type={'number'}
                 min={0}
                 ref={(r) => this.setRankInputRef(r)}
                 onKeyDown={(k) => this.handleKeyPressRankValue(k)}
                 className={'confirm-input'}
                 placeholder={'enter value ...'}
                 onChange={(e) => this.handleRankCellValueChange(e, { cell })}
                 onBlur={(e) => this.handleRankInputFocusOut(e.relatedTarget, cell.row.original.id)}
          />
          <button className={'cancel-option'}
                  onClick={(e) => {
                    this.removeRankItemWithIdFromState(this.state.changedRankValue!.initialValue);
                  }}
                  id={`rank-cancel-${cell.row.original.id}`}>
          </button>
        </div>}
    </div>;
  };

  setRankInputRef = (r: any) => {
    if (r) {
      this.rankInputRef = r;
    }
  };

  handleRankInputFocusOut = (target: any, id: number) => {
    if (target && target.id && target.id === `rank-cancel-${id}`) {
      return;
    }
    const value = this.state.changedRankValue!.value;
    this.removeRankItemWithIdFromState(value);
  };

  handleKeyPressRankValue = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      const value = this.state.changedRankValue!.value;
      this.removeRankItemWithIdFromState(value);
    }
  };

  removeRankItemWithIdFromState = (value?: number) => {
    this.setState((s) => {
      const item = s.changedRankValue!;

      const request: CarModelRequest = item!.rowItem;
      request.rank = value;
      if (value !== item.initialValue) {
        this.props.editCarModel(item!.id, request);
      }

      return {
        ...s,
        changedRankValue: undefined
      };
    });
  };

  addRankItemWithIdAndValue = (id: number, obj: CarModelRequest, value?: number) => {
    this.setState({
        changedRankValue: {
          id: id,
          value: value,
          initialValue: value,
          rowItem: obj
        }
      }
      , () => {
        if (this.rankInputRef) {
          this.rankInputRef.focus();
        }
      });
  };

  handleRankCellValueChange = (e: any, { cell }: { cell: any }) => {
    const targetVal = e.target.value ? Number(e.target.value) : undefined;
    const request: CarModelRequest = cell.row.original;
    request.rank = targetVal;

    if (!this.state.changedRankValue) {
      this.addRankItemWithIdAndValue(cell.row.original.id, request, targetVal);
    } else {
      this.setState((s) => {
        return {
          ...s,
          changedRankValue: {
            ...s.changedRankValue as CarModelRankItem,
            value: targetVal
          }
        };
      });
    }
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = async (newValue: any, field: string) => {
    this.setState((prevState) => {
      return {
        ...prevState,
        filter: {
          ...prevState.filter,
          [ field ]: {
            value: newValue.length === 0 ? { value: undefined, label: 'HAS PRICE' } : newValue
          }
        }
      };
    }, async () => {
      const filters = this.composeCarModelFilters();
      await this.props.getCarModelsList(true, filters);
      this.props.updateCarModelFilters(filters);
    });
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      this.props.getCarModelsList(true, this.composeCarModelFilters(), pageRequest);

      this.setState({
        selectedPageNumber: pageRequest.page + 1,
        sortOrder: pageRequest.sortDirection,
        sortedBy: pageRequest.sortField
      });
    }
  }

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.car_models.acriss'), accessorKey: 'acrissCode', id: 'acrissCode', meta: { width: 150 }
      },
      { header: t('ct.car_models.model'), accessorKey: 'name', id: 'name', meta: { maxWidth: 200 } },
      {
        header: t('ct.car_models.rank'),
        accessorKey: 'rank',
        id: 'rank',
        meta: { width: 100 },
        cell: ({ cell }: { cell: any }) => this.rankCell({ cell })
      },
      { header: t('ct.car_models.drive'), accessorKey: 'driveType', id: 'driveType', meta: { maxWidth: 150 } },
      {
        header: t('ct.car_models.transmission'),
        accessorKey: 'transmissionType',
        id: 'transmissionType',
        meta: { maxWidth: 150 }
      },
      { header: t('ct.car_models.fuel'), accessorKey: 'fuelType', id: 'fuelType', meta: { maxWidth: 150 } },
      {
        header: t('ct.car_models.passangers'),
        accessorKey: 'passengerCount',
        id: 'passengerCount',
        meta: { maxWidth: 150 }
      },
      { header: t('ct.car_models.doors'), accessorKey: 'doorCount', id: 'doorCount', meta: { maxWidth: 150 } },
      {
        header: t('ct.car_models.rate_plans'),
        accessorKey: 'associatedRatePlansNo',
        id: 'associatedRatePlansNo',
        meta: { maxWidth: 250 }
      },
      {
        header: t('ct.car_models.status'),
        accessorKey: 'statusType',
        id: 'statusType',
        meta: { width: 150 },
        cell: (cell: any) => this.statusCell(cell.getValue())
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = CarModelSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: columnName });
    return (
      this.props.getCarModelsList(true, this.composeCarModelFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.car_models')}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, carModelEditRoles) && (
              <Link
                to={{
                  pathname: '/setup/carmodels/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="d-flex justify-content-between container-fluid filter-container form-group form-inline">
            <div className="d-flex ml-1 w-75 filters-container-margin-top-btm">
              <input
                type="text"
                value={this.state.filter.combinedFieldFilter.value}
                name="combinedFieldFilter"
                className="form-control col mr-1 filter-select margins"
                placeholder={t('ph.filter.car_models.by_name')}
                onChange={this.changeHandler}
              />
              <input
                type="text"
                value={this.state.filter.tag.value}
                name="tag"
                className="form-control col mr-1 filter-select margins"
                placeholder={t('ph.filter.car_modesl.by_tag')}
                onChange={this.changeHandler}
              />
              <Select
                className="mr-1"
                placeholder={t('ph.filter.car_modesl.by_purpose')}
                value={this.state.filter.hasPriceFilter.value}
                options={[
                  { value: undefined, label: 'HAS PRICE' },
                  { value: 'false', label: 'ALTERNATIVE' }
                ]}
                equalWidthElement
                onChange={(val) => this.dropdownChange(val, 'hasPriceFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="status"
              />
            </div>
            <div className="pl-1">
              <button type="button" className="btn btn-text-only mr-2"
                      onClick={() => this.clearFilters()}>{t('b.clear_filter')}</button>
              <button type="submit" className="btn btn-primary filter-button"
                      onClick={this.onFilterClick}>{t('b.filter').toUpperCase()}</button>
            </div>
          </form>
          {this.props.carModels
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.carModels}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={this.state.sortedBy}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  carModels: state.setup.carModels.carModels,
  user: state.authentication.user,
  totalPages: state.setup.carModels.totalPages,
  totalElements: state.setup.carModels.totalElements,
  filters: state.setup.carModels.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, CarModelActionTypes>) => ({
  getCarModelsList: (countRatePlans?: boolean, filters?: CarModelListFilters, page?: PageRequest) => dispatch(getCarModelsList(countRatePlans, filters, page)),
  updateCarModelFilters: (filters: CarModelListFilters) => dispatch(updateCarModelFilters(filters)),
  editCarModel: (id: number, request: CarModelRequest) => dispatch(editCarModel(id, request))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(CarModels));
